import { useEffect, useRef } from "react";
import type { ReferenceItem } from "../../api/types.js";
import { SourceCard } from "./SourceCard.js";

export function ReferencePoolTab({
  referencePool,
  loading,
  loaded,
  error,
  hasMore,
  onLoadMore,
  onRetry,
  currentSessionId,
  saveSourceAsAsset
}: {
  referencePool: ReferenceItem[];
  loading: boolean;
  loaded: boolean;
  error: string;
  hasMore: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
  currentSessionId?: string | null;
  saveSourceAsAsset: (source: ReferenceItem) => void | Promise<void>;
}) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loading) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
    }, { root: sentinel.closest(".source-list"), rootMargin: "180px 0px" });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, onLoadMore]);

  if (error && referencePool.length === 0) {
    return <div className="source-pool-state" role="alert"><p>文献池加载失败。</p><button type="button" onClick={onRetry}>重试</button></div>;
  }
  if (loading && !loaded && referencePool.length === 0) {
    return <p className="empty-note source-empty" role="status">正在加载文献池…</p>;
  }
  if (loaded && referencePool.length === 0) {
    return <p className="empty-note source-empty">当前文献池为空。</p>;
  }

  return (
    <>
      {referencePool.map((source) => (
        <SourceCard
          key={`${source.poolId}-${source.citationIndex || source.index || ""}`}
          source={source}
          currentSessionId={currentSessionId}
          onSave={saveSourceAsAsset}
        />
      ))}
      {error && <div className="source-pool-state" role="alert"><p>下一页加载失败。</p><button type="button" onClick={onRetry}>重试</button></div>}
      {loading && referencePool.length > 0 && <p className="source-pool-loading" role="status">正在加载更多…</p>}
      <div className="source-pool-sentinel" ref={sentinelRef} aria-hidden="true" />
    </>
  );
}
